/**
 * invite_organization_member Tool - Invite a user to an organization
 *
 * Sends an email invitation for someone to join an organization.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { OrganizationRole } from '@runhuman/shared';
import type { JobClient } from '../clients/job-client.js';

export interface InviteOrganizationMemberArgs {
  organizationId: string;
  email: string;
  role: OrganizationRole;
}

export const inviteOrganizationMemberToolDefinition: Tool = {
  name: 'invite_organization_member',
  description: `Invite a user to join an organization by email.

Sends an email invitation. The user must accept the invite to become a member. Requires members:manage permission in the organization (admin role).

Parameters:
- organizationId: The organization to invite to
- email: Email address of the person to invite
- role: "admin", "contributor", or "viewer" (default: "contributor")

Roles:
- admin: Full access including billing, member management, and project creation
- contributor: Can edit projects and run jobs, but cannot manage billing or members
- viewer: Read-only access to all organization resources

Typical workflow:
1. list_organizations → Find the organization
2. list_organization_members(organizationId: "...") → Verify current members
3. invite_organization_member(organizationId: "...", email: "...", role: "contributor") → Send invite`,
  inputSchema: {
    type: 'object',
    properties: {
      organizationId: {
        type: 'string',
        description: 'The organization ID to invite the user to',
      },
      email: {
        type: 'string',
        description: 'Email address of the person to invite',
      },
      role: {
        type: 'string',
        enum: ['admin', 'contributor', 'viewer'],
        description: 'Role to assign: "admin", "contributor", or "viewer"',
        default: 'contributor',
      },
    },
    required: ['organizationId', 'email'],
  },
};

export async function handleInviteOrganizationMember(
  args: InviteOrganizationMemberArgs,
  jobClient: JobClient
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  const role: OrganizationRole = args.role || 'contributor';

  try {
    const result = await jobClient.inviteOrganizationMember(args.organizationId, {
      email: args.email,
      role,
    });

    return {
      content: [{
        type: 'text',
        text: `Invitation sent successfully!

Email: ${args.email}
Role: ${role}
Invitation ID: ${result.invitationId}

${result.message}

Next steps:
- List members: list_organization_members(organizationId: "${args.organizationId}")`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };

    return {
      content: [{
        type: 'text',
        text: `Failed to invite member

Error: ${err.message}
${err.status ? `Status: ${err.status}` : ''}

Common causes:
- You must have members:manage permission (admin role) in the organization
- The user may already be a member
- The organization may be at its member limit`,
      }],
      isError: true,
    };
  }
}
